'use client';

import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  Cell,
} from 'recharts';
import { TradeDirection } from './TradeCard';
import { useLang } from '@/lib/i18n/LanguageContext';

interface TradePriceChartProps {
  entryPrice: number;
  stopLoss: number;
  takeProfit1: number;
  takeProfit2?: number;
  takeProfit3?: number;
  direction: TradeDirection;
  ticker: string;
}

function formatPriceShort(price: number): string {
  if (price == null || isNaN(price)) return '';
  if (price >= 10000) return price.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (price >= 100) return price.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 5 });
}

const CustomTooltip = ({ active, payload }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div
      className="px-3 py-2 rounded-lg text-xs"
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        boxShadow: '0 8px 24px rgba(28,43,38,0.18)',
      }}
    >
      <p className="font-semibold mb-0.5" style={{ color: d.color }}>
        {d.label}
      </p>
      <p className="font-mono" style={{ color: 'var(--text-primary)' }}>
        {formatPriceShort(d.price)}
      </p>
      {d.rr && (
        <p className="mt-0.5" style={{ color: 'var(--text-secondary)' }}>
          R:R {d.rr}
        </p>
      )}
    </div>
  );
};

export default function TradePriceChart({
  entryPrice,
  stopLoss,
  takeProfit1,
  takeProfit2,
  takeProfit3,
  direction,
  ticker,
}: TradePriceChartProps) {
  const { t } = useLang();
  const isBuy = direction === 'BUY';

  // Calculate risk and rewards
  const risk = isBuy ? entryPrice - stopLoss : stopLoss - entryPrice;
  const rr1 = risk > 0 ? ((isBuy ? takeProfit1 - entryPrice : entryPrice - takeProfit1) / risk).toFixed(1) : null;
  const rr2 = takeProfit2 && risk > 0 ? ((isBuy ? takeProfit2 - entryPrice : entryPrice - takeProfit2) / risk).toFixed(1) : null;
  const rr3 = takeProfit3 && risk > 0 ? ((isBuy ? takeProfit3 - entryPrice : entryPrice - takeProfit3) / risk).toFixed(1) : null;

  // Build price levels for the chart
  const levels = [
    { label: 'SL', price: stopLoss, color: 'var(--bearish)', type: 'sl', rr: null },
    { label: 'Entry', price: entryPrice, color: 'var(--win)', type: 'entry', rr: null },
    ...(takeProfit3 ? [{ label: 'TP3', price: takeProfit3, color: 'rgba(14,138,77,0.55)', type: 'tp3', rr: rr3 }] : []),
    ...(takeProfit2 ? [{ label: 'TP2', price: takeProfit2, color: 'rgba(14,138,77,0.75)', type: 'tp2', rr: rr2 }] : []),
    { label: 'TP1', price: takeProfit1, color: 'var(--bullish)', type: 'tp1', rr: rr1 },
  ];

  // Sort by price ascending for chart display
  const sorted = [...levels].sort((a, b) => a.price - b.price);

  // Compute chart domain with padding
  const prices = sorted.map((l) => l.price);
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const range = maxPrice - minPrice;
  const pad = range * 0.25;
  const domainMin = minPrice - pad;
  const domainMax = maxPrice + pad;

  // Build bar data: each level is a thin bar at its price
  // We use a single bar chart with reference lines for each level
  // The bar data represents the "zone" between SL and TP
  const zoneData = [
    {
      name: ticker,
      // The bar spans from SL to TP1 (or highest TP)
      base: isBuy ? stopLoss : takeProfit1,
      height: isBuy
        ? (takeProfit3 ?? takeProfit2 ?? takeProfit1) - stopLoss
        : stopLoss - (takeProfit3 ?? takeProfit2 ?? takeProfit1),
    },
  ];

  // Split zone into risk (SL→Entry) and reward (Entry→TP)
  const riskZone = {
    name: 'Risk',
    base: isBuy ? stopLoss : entryPrice,
    height: risk,
    fill: 'rgba(217,56,67,0.18)',
  };
  const rewardZone = {
    name: 'Reward',
    base: isBuy ? entryPrice : takeProfit1,
    height: isBuy
      ? (takeProfit3 ?? takeProfit2 ?? takeProfit1) - entryPrice
      : entryPrice - (takeProfit3 ?? takeProfit2 ?? takeProfit1),
    fill: 'rgba(14,138,77,0.12)',
  };

  const chartData = [{ ...riskZone }, { ...rewardZone }];

  // Use a simpler horizontal layout: one bar per level
  const barData = sorted.map((l) => ({
    ...l,
    value: 1, // constant width
    displayPrice: formatPriceShort(l.price),
  }));

  return (
    <div
      className="rounded-xl overflow-hidden mb-4"
      style={{ border: '1px solid var(--border)', background: 'var(--bg-sunken)' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ borderColor: 'var(--border)' }}
      >
        <span className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>
          {t('Price Levels')}
        </span>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bearish)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>SL</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--win)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Entry</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full" style={{ background: 'var(--bullish)' }} />
            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>TP</span>
          </div>
        </div>
      </div>

      {/* Chart */}
      <div style={{ height: 120, padding: '8px 0 4px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={barData}
            layout="vertical"
            margin={{ top: 4, right: 80, bottom: 4, left: 8 }}
          >
            <XAxis
              type="number"
              domain={[domainMin, domainMax]}
              hide
            />
            <YAxis
              type="category"
              dataKey="label"
              width={36}
              tick={{ fill: 'var(--text-muted)', fontSize: 10, fontFamily: 'Instrument Sans' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: 'var(--bg-card-hover)' }} />

            {/* Background zone: risk (red) */}
            <ReferenceLine
              x={stopLoss}
              stroke="var(--bearish)"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              opacity={0.6}
            />
            {/* Entry line */}
            <ReferenceLine
              x={entryPrice}
              stroke="var(--win)"
              strokeWidth={2}
              opacity={0.9}
            />
            {/* TP1 */}
            <ReferenceLine
              x={takeProfit1}
              stroke="var(--bullish)"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              opacity={0.7}
            />
            {takeProfit2 && (
              <ReferenceLine
                x={takeProfit2}
                stroke="var(--bullish)"
                strokeWidth={1}
                strokeDasharray="2 4"
                opacity={0.5}
              />
            )}
            {takeProfit3 && (
              <ReferenceLine
                x={takeProfit3}
                stroke="var(--bullish)"
                strokeWidth={1}
                strokeDasharray="2 4"
                opacity={0.4}
              />
            )}

            {/* Bars: one per level, colored by type */}
            <Bar dataKey="price" barSize={8} radius={[0, 4, 4, 0]}>
              {barData.map((entry) => (
                <Cell key={entry.label} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* Price labels row */}
      <div
        className="flex items-center justify-around px-3 py-2 border-t"
        style={{ borderColor: 'var(--border)' }}
      >
        <div className="text-center">
          <div className="text-xs mb-0.5" style={{ color: 'var(--bearish)' }}>SL</div>
          <div className="font-mono text-xs font-semibold" style={{ color: 'var(--bearish)' }}>
            {formatPriceShort(stopLoss)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs mb-0.5" style={{ color: 'var(--win)' }}>Entry</div>
          <div className="font-mono text-xs font-semibold" style={{ color: 'var(--win)' }}>
            {formatPriceShort(entryPrice)}
          </div>
        </div>
        <div className="text-center">
          <div className="text-xs mb-0.5" style={{ color: 'var(--bullish)' }}>TP1</div>
          <div className="font-mono text-xs font-semibold" style={{ color: 'var(--bullish)' }}>
            {formatPriceShort(takeProfit1)}
          </div>
          {rr1 && (
            <div className="text-xs font-mono" style={{ color: 'var(--accent)' }}>1:{rr1}R</div>
          )}
        </div>
        {takeProfit2 && (
          <div className="text-center">
            <div className="text-xs mb-0.5" style={{ color: 'var(--bullish)' }}>TP2</div>
            <div className="font-mono text-xs font-semibold" style={{ color: 'var(--bullish)' }}>
              {formatPriceShort(takeProfit2)}
            </div>
            {rr2 && (
              <div className="text-xs font-mono" style={{ color: 'var(--accent)' }}>1:{rr2}R</div>
            )}
          </div>
        )}
        {takeProfit3 && (
          <div className="text-center">
            <div className="text-xs mb-0.5" style={{ color: 'var(--bullish)' }}>TP3</div>
            <div className="font-mono text-xs font-semibold" style={{ color: 'var(--bullish)' }}>
              {formatPriceShort(takeProfit3)}
            </div>
            {rr3 && (
              <div className="text-xs font-mono" style={{ color: 'var(--accent)' }}>1:{rr3}R</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
